import React, { useState } from "react";
import { Controller, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { AxiosError } from "axios";
import axios from "axios";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { SlideOut } from "../../../../components/SlideOut/SlideOut";
import {
  ConfirmOrCancelButtonContainer,
  DeleteButton,
  SecondaryButtonWithPlusIcon,
} from "../../../../components/Buttons/Buttons";
import { SearchSelectInfiniteScroll } from "../../../../components/SearchSelectInfiniteScroll/SearchSelectInfiniteScroll";
import { endpoints } from "../../../../endpoints";
import { useNotifications } from "../../../../components/Notifications/NotificationsContext";
import { useFormWrapper, useStoreState } from "../../../../util/util";
import { Form } from "../../../../layout/FormLayout";
import type {
  PIMProductSubscriberArgs,
  User,
  Product,
  PIMProductSubscriber,
  PIMProductSubscriberPaginatedOutput,
} from "../../../../types/types";
import {
  zodSelectBoxDefault,
  zodSelectBoxType,
} from "../../../../util/zod.util";
import { H3, H6 } from "../../../../components/Typography/Typography";
import type { DataMutate } from "../../../../types/types"; // For mutateSubscribersList
import { FullWidthSelectWithDeleteButton } from "../../../admin/SellerAdmin/SellerAdminTeams/SellerAdminCreateNewTeam"; // Reuse styled component
import { findNonEmptyDuplicateIndexes } from "../../../../util/form.utils";

// Schema for multiple user selection using useFieldArray
const SubscribersSchemaBase = z.object({
  first_subscriber: zodSelectBoxType, // At least one subscriber needed
  subscribers: z
    .object({
      user: zodSelectBoxType,
    })
    .array()
    .optional(),
});

const SubscribersSchema = (t: (s: string) => string) =>
  z
    .object({
      first_subscriber: zodSelectBoxDefault(t),
      subscribers: z
        .object({
          user: zodSelectBoxDefault(t),
        })
        .array()
        .optional(),
    })
    .superRefine(({ first_subscriber, subscribers }, ctx) => {
      // Check for duplicates including the first subscriber
      const duplicateUserIdxs = subscribers
        ? findNonEmptyDuplicateIndexes([
            first_subscriber.value,
            ...subscribers.map(({ user: { value } }) => value),
          ])
        : [];

      if (duplicateUserIdxs.length > 0) {
        duplicateUserIdxs.forEach((idx) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("Cannot add the same user twice"),
            // Adjust path based on whether it's the first user or in the array
            path: [
              idx === 0 ? "first_subscriber" : `subscribers[${idx - 1}].user`,
            ],
          });
        });
      }
    });

type FormOutput = z.infer<typeof SubscribersSchemaBase>;

type ProductSubscribersSelectorProps = {
  isOpen: boolean;
  onClose: () => void;
  product: Product;
  mutateSubscribersList: DataMutate<PIMProductSubscriberPaginatedOutput>; // SWR mutate fn
};

export const ProductSubscribersSelector = ({
  isOpen,
  onClose,
  product,
  mutateSubscribersList,
}: ProductSubscribersSelectorProps) => {
  const { t } = useTranslation();
  const { notifySuccess, notifyError } = useNotifications();
  const { tenant_id, storefront_id } = useStoreState();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { handleSubmit, control, formState, reset } =
    useFormWrapper<FormOutput>({
      resolver: zodResolver(SubscribersSchema(t)),
      defaultValues: {
        first_subscriber: { label: "", value: "" },
        subscribers: [],
      },
      reValidateMode: "onChange",
    });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "subscribers",
  });

  const handleClose = () => {
    reset({ first_subscriber: { label: "", value: "" }, subscribers: [] }); // Reset form on close
    onClose();
  };

  const onSubmit = async (values: FormOutput) => {
    setIsSubmitting(true);
    const subscriberIds = [
      values.first_subscriber.value,
      ...(values.subscribers?.map(({ user }) => user.value) ?? []),
    ].filter(Boolean); // Ensure no empty values

    if (subscriberIds.length === 0) {
      notifyError(t("Please select at least one subscriber"));
      setIsSubmitting(false);
      return;
    }

    const payload: PIMProductSubscriberArgs = {
      subscriber_ids: subscriberIds as string[], // Cast as string array
    };

    try {
      await axios.post<PIMProductSubscriber[]>(
        endpoints.v2_storefronts_id_pim_products_id_subscribers(
          storefront_id,
          product.id
        ),
        payload
      );
      await mutateSubscribersList();
      notifySuccess(t("Subscribers added successfully"));
      handleClose(); // Close and reset form
    } catch (error) {
      const axiosError = error as AxiosError;
      const errorMessage =
        axiosError?.response?.data?.message || t("Failed to add subscribers");
      notifyError(errorMessage, { error });
    } finally {
      setIsSubmitting(false);
    }
  };

  const userSearchParams = new URLSearchParams();
  userSearchParams.append("order_by", "asc");

  const createUserFields = (): JSX.Element[] => {
    return fields.map((field, index: number) => (
      <FullWidthSelectWithDeleteButton key={field.id}>
        <Controller
          control={control}
          name={`subscribers[${index}].user`}
          render={(props) => (
            <SearchSelectInfiniteScroll
              {...props}
              placeholder={t("Select User")}
              errors={{
                [`subscribers[${index}].user`]:
                  formState.errors?.subscribers?.[index]?.user?.value ??
                  formState.errors?.subscribers?.[index]?.user ??
                  undefined,
              }}
              formState={formState}
              baseUrl={endpoints.v1_storefronts_id_tenants_id_users(
                storefront_id,
                tenant_id
              )}
              params={userSearchParams}
              getOptions={(response: User[]) =>
                response.map((user) => ({
                  value: user.id,
                  label: `${user.firstname} ${user.lastname}`,
                }))
              }
            />
          )}
        />
        <DeleteButton
          data-testid={`delete-subscriber-${index}`}
          onClick={() => remove(index)}
          type="button"
        />
      </FullWidthSelectWithDeleteButton>
    ));
  };

  return (
    <SlideOut show={isOpen} closeFlyout={handleClose}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <H3>{t("Add Product Subscribers")}</H3>
        <H6>{t("Users")}</H6>
        <Controller
          control={control}
          name="first_subscriber"
          render={(props) => (
            <SearchSelectInfiniteScroll
              {...props}
              label={t("Add Subscriber")}
              errors={formState.errors}
              formState={formState}
              placeholder={t("Select User")}
              baseUrl={endpoints.v1_storefronts_id_tenants_id_users(
                storefront_id,
                tenant_id
              )}
              params={userSearchParams}
              getOptions={(response: User[]) =>
                response.map((user) => ({
                  value: user.id,
                  label: `${user.firstname} ${user.lastname}`,
                }))
              }
            />
          )}
        />

        {createUserFields()}

        <SecondaryButtonWithPlusIcon
          type="button"
          style={{ marginTop: "10px" }}
          onClick={() => append({ user: { label: "", value: "" } })}
          data-testid="add-another-subscriber-button"
        >
          {t("Add another user")}
        </SecondaryButtonWithPlusIcon>
        <ConfirmOrCancelButtonContainer
          onCancel={handleClose}
          onConfirm={handleSubmit(onSubmit)}
          isConfirmLoading={isSubmitting}
          isConfirmDisabled={isSubmitting}
        />
      </Form>
    </SlideOut>
  );
};
